import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Workflow, Zap, Link2, GraduationCap, CheckCircle2 } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
const STEPS = [{
  icon: Workflow,
  title: 'Discovery & Mapping',
  desc: 'We sit down with your team to understand your unique processes, bottlenecks, and the tools you rely on daily.'
}, {
  icon: Zap,
  title: 'Custom Build-out',
  desc: 'Our engineers design and deploy custom automations and agents tailored specifically to your mapped workflows.'
}, {
  icon: Link2,
  title: 'Deep Integration',
  desc: 'We wire Giga directly into your existing stack—whether it’s legacy on-prem systems or modern SaaS.'
}, {
  icon: GraduationCap,
  title: 'Training & Tuning',
  desc: 'We onboard your team, run workshops, and continuously refine the agents as your operations evolve.'
}];
const OUTCOMES = [{
  title: 'Automated Client Reporting',
  desc: 'Giga pulls data from Salesforce, Stripe, and internal databases to generate and email weekly client reports without human intervention.'
}, {
  title: 'RevOps Pipelines',
  desc: 'Seamlessly sync lead data across marketing and sales tools, automatically triggering personalized outreach sequences.'
}, {
  title: 'Support Triage',
  desc: 'Intelligently categorize, route, and draft initial responses for incoming support tickets based on historical resolution data.'
}, {
  title: 'Finance Reconciliation',
  desc: 'Automatically match invoices with bank statements and flag discrepancies for human review, saving hours of manual data entry.'
}];
export function Bespoke() {
  useScreenInit();
  return <div className="min-h-screen bg-[#1C1510] flex flex-col pt-24 pb-12 relative overflow-hidden" data-id="element-6004" data-mp="kuksttcco19h">
      {/* Dark texture overlay */}
      <div className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none z-0" data-id="element-6005" data-mp="1iarwgkv2uf8b" />

      {/* Soft warm glow */}
      <div aria-hidden="true" className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full bg-[#F97316]/[0.08] blur-[120px] z-0" data-id="element-6006" data-mp="1dhp2ibme13rl" />

      <div className="relative z-10 flex-1 flex flex-col" data-id="element-6007" data-mp="21da9fip664u4">
        {/* Hero Section */}
        <section className="px-6 md:px-10 py-16 md:py-24 max-w-5xl mx-auto text-center" data-id="element-6008" data-mp="1nnqayp2mftqm">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-6009" data-mp="1ro0k2alpn09p">
            <span className="inline-block text-xs font-bold tracking-[3px] uppercase text-[#EA580C] mb-6" data-id="element-6010" data-mp="1ioddlae4j49s">
              Custom Engagement
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-title font-bold text-white tracking-tight leading-[1.1] mb-8" data-id="element-6011" data-mp="14q5m3hzuwv60">
              Your workflows, <br className="hidden md:block" data-id="element-6012" data-mp="2z1kfc8s2tcqi" />
              <span className="text-neutral-400" data-id="element-6013" data-mp="2z2vefvaktwba">built by our team.</span>
            </h1>
            <p className="text-lg md:text-xl text-neutral-400 leading-relaxed max-w-2xl mx-auto mb-10" data-id="element-6014" data-mp="3335njgto12ud">
              For teams with complex operations. We don't just hand you a tool;
              we partner with you to map, build, and deploy custom AI agents
              that integrate deeply into your stack.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-6015" data-mp="117zuigsyl2kt">
              <Link to="/demo/book" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-[#EA580C] hover:bg-[#C2410C] text-white font-bold transition-colors shadow-lg shadow-[#EA580C]/20 flex items-center justify-center gap-2" data-id="element-6016" data-mp="wdc42cee9y3l">
                Book a call
                <ArrowRight size={18} data-id="element-6017" data-mp="1k3y9rx0xf6af" />
              </Link>
              <Link to="/contact" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold transition-colors flex items-center justify-center" data-id="element-6018" data-mp="1f5wor6jxwwwb">
                Contact us
              </Link>
            </div>
          </motion.div>
        </section>

        {/* How it Works */}
        <section className="px-6 md:px-10 py-16 md:py-24 bg-[#2B211A]/50 border-y border-white/5" data-id="element-6019" data-mp="3jb6a77aa4r1y">
          <div className="max-w-6xl mx-auto" data-id="element-6020" data-mp="3nc8bdtnrbd6k">
            <div className="text-center mb-16" data-id="element-6021" data-mp="3scmm5jgqt18a">
              <h2 className="text-3xl md:text-4xl font-title font-bold text-white mb-4" data-id="element-6022" data-mp="3sdxpji6a06yv">
                How the engagement works
              </h2>
              <p className="text-neutral-400 max-w-2xl mx-auto" data-id="element-6023" data-mp="2znyvf8d85jnc">
                A structured, hands-on approach to automating your most critical
                business processes.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-6024" data-mp="39mek74os4gto">
              {STEPS.map((step, i) => {
              const Icon = step.icon;
              return <motion.div key={i} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.5,
                delay: i * 0.1
              }} className="bg-white/5 border border-white/10 rounded-2xl p-6 relative overflow-hidden group hover:border-[#EA580C]/50 transition-colors" data-id="element-6025" data-mp="3eexxe0jk19xy" data-mpi={i}>
                    <div className="absolute top-0 right-0 p-4 text-[100px] font-title font-bold text-white/[0.02] leading-none select-none pointer-events-none group-hover:text-[#EA580C]/[0.05] transition-colors" data-id="element-6026" data-mp="1zfscq1ihiyho">
                      {i + 1}
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-[#1C1510] border border-white/10 flex items-center justify-center mb-6 relative z-10" data-id="element-6027" data-mp="1qm5joj5nw9c6">
                      <Icon className="w-6 h-6 text-[#EA580C]" data-id="element-6028" data-mp="1gpsrxqtog5k2" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3 relative z-10" data-id="element-6029" data-mp="1bpy1fodtdhzj">
                      {step.title}
                    </h3>
                    <p className="text-sm text-neutral-400 leading-relaxed relative z-10" data-id="element-6030" data-mp="17p4f2zg0sb8r">
                      {step.desc}
                    </p>
                  </motion.div>;
            })}
            </div>
          </div>
        </section>

        {/* Outcomes */}
        <section className="px-6 md:px-10 py-16 md:py-24 max-w-6xl mx-auto" data-id="element-6031" data-mp="o3kq5ctapb9a">
          <div className="mb-16" data-id="element-6032" data-mp="t1m79s75v0i4">
            <h2 className="text-3xl md:text-4xl font-title font-bold text-white mb-4" data-id="element-6033" data-mp="t2xek1zteqdz">
              What we build
            </h2>
            <p className="text-neutral-400 max-w-2xl" data-id="element-6034" data-mp="1gtky5kj3f4gd">
              Examples of custom solutions we've deployed for enterprise
              partners.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6035" data-mp="1715dgfgew1rs">
            {OUTCOMES.map((outcome, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: i % 2 === 0 ? -20 : 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5
          }} className="flex gap-4 p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/[0.07] transition-colors" data-id="element-6036" data-mp="1bz6zn994wh7h" data-mpi={i}>
                <div className="mt-1 shrink-0" data-id="element-6037" data-mp="29sg93sfrju2q">
                  <CheckCircle2 className="w-6 h-6 text-[#EA580C]" data-id="element-6038" data-mp="2nkmj0wsbp4tq" />
                </div>
                <div data-id="element-6039" data-mp="2inm9ud06flye">
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-6040" data-mp="2in4fjlx2rht4">
                    {outcome.title}
                  </h3>
                  <p className="text-neutral-400 text-sm leading-relaxed" data-id="element-6041" data-mp="1uytm33tpkkev">
                    {outcome.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </section>

        {/* Proof & Closing CTA */}
        <section className="px-6 md:px-10 py-16 md:py-24 bg-[#EA580C] text-center mt-auto" data-id="element-6042" data-mp="1zv1ve0l85je4">
          <div className="max-w-3xl mx-auto" data-id="element-6043" data-mp="3g9jxspxbsjhq">
            <h2 className="text-3xl md:text-5xl font-title font-bold text-white mb-6" data-id="element-6044" data-mp="3gcfvcd59h65n">
              Ready to automate your operations?
            </h2>
            <p className="text-lg text-white/80 mb-10" data-id="element-6045" data-mp="19mvugb8ph6aj">
              Let's discuss your workflows and see if a Custom engagement is
              right for your team.
            </p>
            <Link to="/demo/book" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#1C1510] hover:bg-black text-white font-bold transition-colors shadow-xl" data-id="element-6046" data-mp="1im93x3wtxuv9">
              Talk to us
              <ArrowRight size={18} data-id="element-6047" data-mp="1sd3qk9dzmwh8" />
            </Link>
          </div>
        </section>
      </div>
    </div>;
}